import { useId, useRef, useState } from "react";
import { useMyMedia, useUploadMedia } from "../../lib/media";

/**
 * Media picker. Three tabs:
 *   - Upload   — drag-drop / click-to-select, hits POST /api/media
 *   - Library  — your previously uploaded files of the right kind
 *   - URL      — paste an external URL (the legacy path; useful for embeds)
 *
 * Calls `onPick(url, alt?)` with the chosen URL. The component is purely a
 * picker — the parent block owns whatever state the URL feeds into.
 */
export function MediaPicker({
  kind,
  currentUrl,
  onPick,
  onCancel,
}: {
  kind: "image" | "audio" | "pdf";
  currentUrl?: string;
  onPick: (url: string, meta?: { alt?: string; filename?: string; bytes?: number }) => void;
  onCancel?: () => void;
}) {
  const [tab, setTab] = useState<"upload" | "library" | "url">(currentUrl ? "url" : "upload");

  return (
    <div className="media-picker">
      <nav className="media-picker-tabs" role="tablist" aria-label="Choose media source">
        <button
          type="button"
          className={`subtab ${tab === "upload" ? "is-active" : ""}`}
          onClick={() => setTab("upload")}
        >
          Upload
        </button>
        <button
          type="button"
          className={`subtab ${tab === "library" ? "is-active" : ""}`}
          onClick={() => setTab("library")}
        >
          Library
        </button>
        <button
          type="button"
          className={`subtab ${tab === "url" ? "is-active" : ""}`}
          onClick={() => setTab("url")}
        >
          By URL
        </button>
        {onCancel && (
          <button type="button" className="row-action" onClick={onCancel} style={{ marginLeft: "auto" }}>
            Close
          </button>
        )}
      </nav>

      {tab === "upload" && <UploadPane kind={kind} onPick={onPick} />}
      {tab === "library" && <LibraryPane kind={kind} onPick={onPick} />}
      {tab === "url" && <UrlPane initial={currentUrl ?? ""} onPick={onPick} />}
    </div>
  );
}

function UploadPane({
  kind,
  onPick,
}: {
  kind: "image" | "audio" | "pdf";
  onPick: (url: string, meta?: { alt?: string; filename?: string; bytes?: number }) => void;
}) {
  const upload = useUploadMedia();
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();

  const accept =
    kind === "image"
      ? "image/jpeg,image/png,image/webp,image/avif,image/gif"
      : kind === "audio"
        ? "audio/mpeg,audio/mp3,audio/ogg,audio/wav,audio/webm"
        : "application/pdf";

  const onFiles = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    try {
      const m = await upload.mutateAsync({ file });
      onPick(m.url, { filename: m.originalFilename ?? undefined, bytes: m.bytes });
    } catch {
      // upload.error is shown below
    }
  };

  return (
    <div
      className={`media-upload-zone ${dragOver ? "is-drag-over" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        onFiles(e.dataTransfer.files);
      }}
    >
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        className="media-upload-input"
        onChange={(e) => onFiles(e.currentTarget.files)}
      />
      <label htmlFor={inputId} className="media-upload-label">
        {upload.isPending ? (
          <span>Uploading…</span>
        ) : (
          <>
            <span className="media-upload-headline">
              Drop {kind === "image" ? "an image" : kind === "audio" ? "an audio file" : "a PDF"} here
            </span>
            <span className="media-upload-sub">or click to choose a file</span>
          </>
        )}
      </label>
      {upload.isError && (
        <p className="media-upload-error">— {upload.error?.message ?? "Upload failed."}</p>
      )}
    </div>
  );
}

function LibraryPane({
  kind,
  onPick,
}: {
  kind: "image" | "audio" | "pdf";
  onPick: (url: string, meta?: { alt?: string; filename?: string; bytes?: number }) => void;
}) {
  const list = useMyMedia(kind);
  const items = list.data?.media ?? [];

  if (list.isLoading) return <p className="media-library-empty">Loading library…</p>;
  if (items.length === 0)
    return (
      <p className="media-library-empty">
        Nothing uploaded yet. Switch to the Upload tab to add your first {kind}.
      </p>
    );

  return (
    <ul className="media-library-grid">
      {items.map((m) => (
        <li key={m.id}>
          <button
            type="button"
            className="media-library-item"
            onClick={() =>
              onPick(m.url, {
                alt: m.alt ?? undefined,
                filename: m.originalFilename ?? undefined,
                bytes: m.bytes,
              })
            }
            title={m.originalFilename ?? m.url}
          >
            {kind === "image" && <img src={m.url} alt={m.alt ?? ""} loading="lazy" />}
            {kind !== "image" && (
              <span className="media-library-icon" aria-hidden>
                {kind === "audio" ? "♪" : "📄"}
              </span>
            )}
            <span className="media-library-meta">
              <span className="media-library-name">{m.originalFilename ?? "untitled"}</span>
              <span className="media-library-size">{formatBytes(m.bytes)}</span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function UrlPane({
  initial,
  onPick,
}: {
  initial: string;
  onPick: (url: string) => void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <form
      className="media-url-pane"
      onSubmit={(e) => {
        e.preventDefault();
        if (value.trim()) onPick(value.trim());
      }}
    >
      <input
        type="url"
        className="auth-input"
        placeholder="https://…"
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <button type="submit" className="btn-app" disabled={!value.trim()}>
        Use this URL
      </button>
    </form>
  );
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}
